import { __assign } from 'tslib';
import React__default, { useRef, useState, useEffect } from 'react';
import { fetcherModeAtom, FetcherMode, useDataViewElement, dataViewListSize, useDataViewElementButtons, useDataViewElementLinks, ListViewSize, ActionValue } from '@keepeek/commons';
import { TableRow, TableCell } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';

var PREFIX = "ListDataViewRow";
var classes = {
    elementRow: "".concat(PREFIX, "-elementRow"),
    mediaCell: "".concat(PREFIX, "-mediaCell"),
    visualAlertCell: "".concat(PREFIX, "-visualAlertCell"),
    linksCell: "".concat(PREFIX, "-linksCell"),
    actionsCell: "".concat(PREFIX, "-actionsCell"),
    fieldCell: "".concat(PREFIX, "-fieldCell")
};
var CellType;
(function (CellType) {
    CellType["MEDIA"] = "previewUrl";
    CellType["LINKS"] = "links";
    CellType["ACTIONS"] = "actions";
})(CellType || (CellType = {}));
var ListDataViewRow = function ListDataViewRow(_a) {
    var row = _a.row, sentryCallback = _a.sentryCallback, openElementCallback = _a.openElementCallback, shareTokenDownloadAuthorized = _a.shareTokenDownloadAuthorized, enabledActions = _a.enabledActions, dataViewKey = _a.dataViewKey, filters = _a.filters, allowNavigation = _a.allowNavigation, onDoubleClick = _a.onDoubleClick, onActionClick = _a.onActionClick, actionButtonsColor = _a.actionButtonsColor, _b = _a.attachmentsVisible, attachmentsVisible = _b === void 0 ? true : _b;
    var element = row.original;
    var rowRef = useRef(null);
    var actionsRef = useRef(null);
    var _c = useState(false), hover = _c[0], setHover = _c[1];
    var _d = useState(false), isEntered = _d[0], setIsEntered = _d[1];
    var _e = useState(false), focus = _e[0], setFocus = _e[1];
    var _f = useState(false), focusLastAction = _f[0], setFocusLastAction = _f[1];
    var fetcherMode = useRecoilValue(fetcherModeAtom);
    var isShare = fetcherMode === FetcherMode.SHARE;
    var _g = useDataViewElement({
        key: dataViewKey,
        id: element.id,
        sentryCallback: sentryCallback,
        openElementCallback: openElementCallback,
        filters: filters
    }), isSelected = _g.isSelected, selectionMode = _g.selectionMode, click = _g.click, doubleClick = _g.doubleClick, actionClick = _g.actionClick, majClick = _g.majClick, associationClick = _g.associationClick;
    useEffect(function () {
        var _a;
        setIsEntered(focus || ((_a = rowRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) || false);
    }, [focus]);
    useEffect(function () {
        var _a;
        if (focusLastAction) {
            var buttons = (_a = actionsRef === null || actionsRef === void 0 ? void 0 : actionsRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("button");
            if (buttons && buttons.length > 0) {
                buttons.item(buttons.length - 1).focus();
                setFocusLastAction(false);
            }
        }
    }, [focusLastAction, isEntered]); // Need isEntered to force last action focus when actions are rendered
    function actionClickWithoutPropagation(value, e) {
        e.stopPropagation();
        actionClick(value);
        onActionClick(value);
    }
    function associationClickWithoutPropagation(e) {
        e.stopPropagation();
        associationClick();
    }
    function handleMediaClick(e) {
        if (selectionMode) {
            // Detect keyboard shortcut
            // Shift pressed during the click
            if (e.shiftKey) {
                majClick();
            }
            else {
                click();
            }
        }
        else {
            actionClick(ActionValue.OPEN_ELEMENT);
            onActionClick(ActionValue.OPEN_ELEMENT);
        }
    }
    var listViewSize = useRecoilValue(dataViewListSize);
    var onSelection = function (e) {
        if (!selectionMode) {
            e.stopPropagation();
            click();
        }
        if (isSelected) {
            // Disable hover actions while uncheking selection button.
            // This will keep focus on selection button allowing keyboard navigation.
            setIsEntered(false);
        }
    };
    var _h = useDataViewElementButtons({
        key: dataViewKey,
        id: element.id,
        isShare: isShare,
        shareTokenDownloadAuthorized: shareTokenDownloadAuthorized,
        enabledActions: enabledActions,
        filters: filters,
        attachmentsVisible: attachmentsVisible
    }), showAttachmentButton = _h.showAttachmentButton, showAddToBasketButton = _h.showAddToBasketButton, showRemoveFromBasketButton = _h.showRemoveFromBasketButton, showShareButton = _h.showShareButton, showDownloadButton = _h.showDownloadButton, showInsertButton = _h.showInsertButton, showThumbnailGeneratorButton = _h.showThumbnailGeneratorButton;
    var _j = useDataViewElementLinks({
        key: dataViewKey,
        id: element.id,
        filters: filters,
        attachmentsVisible: attachmentsVisible
    }), showMediaLinkLink = _j.showMediaLinkLink, showAttachmentLink = _j.showAttachmentLink;
    var listViewSizeValue;
    switch (listViewSize) {
        case ListViewSize.L:
            listViewSizeValue = "120px";
            break;
        case ListViewSize.XL:
            listViewSizeValue = "200px";
            break;
        default:
            console.error("This size ".concat(listViewSize, " is not implemented"));
            listViewSizeValue = "120px";
    }
    return (React__default.createElement(TableRow, { hover: true, className: classes.elementRow, onMouseEnter: function () { return setHover(true); }, onMouseLeave: function () { return setHover(false); }, onFocus: function () { return setFocus(true); }, onBlur: function () { return setFocus(false); }, ref: rowRef, sx: {
            cursor: allowNavigation ? "pointer" : "default",
            // Without this when we select multiples medias with shift key, all text between them are also selected
            WebkitUserSelect: selectionMode ? "none" : "auto",
            MsUserSelect: selectionMode ? "none" : "auto",
            userSelect: selectionMode ? "none" : "auto"
        } }, row.getVisibleCells().map(function (cell) {
        switch (cell.column.id) {
            case CellType.MEDIA:
                return (React__default.createElement(TableCell, { onClick: handleMediaClick, onDoubleClick: function () {
                        doubleClick();
                        onDoubleClick();
                    }, key: cell.id, sx: {
                        pb: 0,
                        pt: 0,
                        pr: 1,
                        pl: 1,
                        minWidth: listViewSizeValue,
                        width: listViewSizeValue
                    }, className: classes.mediaCell }, flexRender(cell.column.columnDef.cell, __assign(__assign({}, cell.getContext()), { hover: hover, onSelection: onSelection, isSelected: isSelected, selectionMode: selectionMode, isEntered: isEntered }))));
            case CellType.LINKS:
                return (React__default.createElement(TableCell, { key: cell.id, sx: { pr: 1, maxWidth: "250px" }, className: classes.linksCell, style: { minWidth: 150 } }, flexRender(cell.column.columnDef.cell, __assign(__assign({}, cell.getContext()), { actionClick: actionClickWithoutPropagation, associationClick: associationClickWithoutPropagation, showAttachmentLink: showAttachmentLink, showMediaLinkLink: showMediaLinkLink }))));
            case CellType.ACTIONS:
                return (React__default.createElement(TableCell, { key: cell.id, sx: { pr: 1, maxWidth: "170px" }, className: classes.actionsCell, ref: actionsRef, tabIndex: isEntered || hover ? -1 : 0, onFocus: function (e) {
                        if (e.target === actionsRef.current) {
                            setFocusLastAction(true);
                        }
                    } }, flexRender(cell.column.columnDef.cell, __assign(__assign({}, cell.getContext()), { showAttachmentButton: showAttachmentButton, showAddToBasketButton: showAddToBasketButton, showRemoveFromBasketButton: showRemoveFromBasketButton, showShareButton: showShareButton, showDownloadButton: showDownloadButton, showInsertButton: showInsertButton, selectionMode: selectionMode, showThumbnailGeneratorButton: showThumbnailGeneratorButton, actionClick: actionClickWithoutPropagation, actionButtonsColor: actionButtonsColor, disableElementActionsAnimation: true }))));
            default:
                return (React__default.createElement(TableCell, { key: cell.id, className: clsx(classes.fieldCell, "".concat(PREFIX, "-").concat(cell.column.id, "Cell")), sx: { ml: 1, overflowWrap: "break-word" }, style: { minWidth: 150 } }, flexRender(cell.column.columnDef.cell, cell.getContext())));
        }
    })));
};

export { CellType, ListDataViewRow as default };
//# sourceMappingURL=index.js.map
